import {
  HttpClient,
  HttpParams,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';

import { Observable, identity, throwError, timer } from 'rxjs';
import { UserService } from './user.service';
import { map, catchError, retry } from 'rxjs/operators';
import { ErrorHandlerService } from './error-handler.service';

/**
 * Whether a failed request is worth sending again unchanged: no response at
 * all (network drop, status 0 or missing, a timeout), a 5xx, or 408/429,
 * which say "try again". Any other 4xx is the server's answer and repeats
 * identically, so retrying it only adds load. A 2xx that failed to parse is
 * not retried either: for a write it proves the server already committed.
 *
 * Reads `status` from whatever reached the retry: an HttpErrorResponse, the
 * HttpErrorInterceptor's { status, message } model (status absent for an
 * ErrorEvent), or an RxJS TimeoutError (no status).
 */
export function isTransientHttpError(error: any): boolean {
  const status = error?.status;
  return !status || status >= 500 || status === 408 || status === 429;
}

/**
 * A retry `delay` that waits `ms` before retrying a transient failure and
 * rethrows anything else, ending the retry at once.
 */
export function retryTransientAfter(ms: number) {
  return (error: any) =>
    isTransientHttpError(error) ? timer(ms) : throwError(() => error);
}

/** Per-request options for reads. */
export interface HttpReadOptions {
  /**
   * false skips the client's own retry, for callers that run their own
   * bounded retry around the request (the calendar load). Stacking both
   * multiplies the attempts per endpoint.
   */
  retry?: boolean;
}

export class SalonmonsterHttpClient {
  /** Two more attempts, one second apart, for transient failures only. */
  private static readonly TRANSIENT_RETRY = {
    count: 2,
    delay: retryTransientAfter(1000),
  };

  /**
   * URL fragments whose POSTs must never be auto-retried, because the server
   * applies a side effect that a second identical request would apply again.
   * stockOrder and stockReturn adjust product stock by a delta, so a retry
   * after a 500 thrown post-commit re-applies the adjustment; stripe charges
   * and bulkEmail sends for the same reason.
   */
  private static readonly NON_RETRYABLE_POST_URLS = [
    'stripe',
    'bulkemail',
    'stockorder',
    'stockreturn',
    // Completing a physical count applies its stock corrections, and a
    // retried completion applies them again. Creating a count is a create:
    // a retry after a lost response leaves two counts in the history. A
    // tracker adjustment (POST /inventoryhistory) writes a ledger row per
    // request, so a retry after a post-commit failure duplicates it.
    'inventorycounts',
    'inventoryhistory',
    // A resend is not idempotent: each attempt puts another copy of the
    // message in front of the client. Retrying a transient failure twice
    // turns one tap into three delivered messages while the UI reports an
    // error — the worst possible combination.
    'resend',
    // Sending a co-worker invitation emails the invitee and counts against
    // the sender's daily cap. A retry after a post-commit failure is refused
    // by the API as a duplicate, so the sender would see an error for an
    // invitation that already went.
    'co-worker-invitations',
    // Accepting a request is a state transition, not an idempotent write: the
    // server commits the status flip and both connection rows together, so a
    // retry after a lost response is refused by the from-pending guard. The
    // recipient would be told the accept failed for one that succeeded.
    // Deleting a request has the same shape.
    'connection-requests',
    // Creating a sale mints a new one per request: a retry after a response
    // lost in flight records the same checkout twice. Also covers
    // POST /sales/:id/receipt, which re-sends the email, and PUT /sales/:id,
    // where resending a Complete re-runs the checkout.
    'sales',
    // Salon-wide SMS copy that two administrators can edit at once. A PUT that
    // commits and then fails on the way back is replayed by the retry,
    // restoring a stale suffix over whatever was saved in between — and
    // neither administrator sees that it happened.
    'customizedtexts',
  ];

  /**
   * Paths whose writes must never be auto-retried, matched against the END of
   * the path (query string stripped) rather than as a fragment.
   */
  private static readonly NON_RETRYABLE_PATH_ENDINGS = [
    // Creating a booking mints a new booking group per request: a retry after
    // a response lost in flight books the client twice, a second or two
    // apart, and both copies send reminders. A 'bookings' fragment would also
    // stop the overlap check and PUT/DELETE /bookings/:id from retrying; only
    // the create (POST /bookings, with or without ?salonID=) ends the path
    // here.
    '/bookings',
    // POST /bookings/:id/email and /reports/email send an email per request:
    // a retry after a failure the server hit post-send delivers extra copies.
    '/email',
    // POST /bookings/:id/sms texts the client per request, same hazard.
    '/sms',
    // Each request emails a fresh reset link; a retry sends duplicates.
    '/passwordreset/stylist/request',
    // Creating a service category (POST /servicecategories) makes a new one
    // per request, so a retry after a lost response adds a duplicate.
    // Renames go to /servicecategories/:id and keep their retry.
    '/servicecategories',
    // Calendar colour saves are safe to repeat, but the screen already shows
    // the new choice. Two retries keep a failure looking saved for about 3
    // seconds before it flips back; failing at once lets the person tap again.
    '/calendar-display-mode',
    '/colour',
    // No-show protection: same reason. A replay a few seconds late could also
    // overwrite a newer save from another admin.
    '/card-on-file',
  ];

  constructor(
    protected http: HttpClient,
    protected userService: UserService,
    protected readonly errorHandlerService: ErrorHandlerService
  ) {}

  /**
   * Whether a WRITE to this URL may be retried.
   *
   * Applies to post, put and delete alike: the hazard is the write landing
   * twice, or landing once and being reported as failed, and that does not
   * depend on the verb. Reads use the narrower check in get().
   */
  private isRetryableWrite(url: string): boolean {
    const lowerUrl = url.toLowerCase();
    const lowerPath = lowerUrl.split('?')[0];
    return (
      !SalonmonsterHttpClient.NON_RETRYABLE_POST_URLS.some((fragment) =>
        lowerUrl.includes(fragment)
      ) &&
      !SalonmonsterHttpClient.NON_RETRYABLE_PATH_ENDINGS.some((ending) =>
        lowerPath.endsWith(ending)
      )
    );
  }

  protected createAuthorizationHeader(
    headers: HttpHeaders = new HttpHeaders(),
    extraHeaders?: Record<string, string>
  ): HttpHeaders {
    let token = this.userService.getToken();
    let result =
      typeof token === 'undefined'
        ? headers.set('Content-Type', 'application/json')
        : headers
            .set('Authorization', this.userService.getToken())
            .set('Content-Type', 'application/json');

    if (extraHeaders) {
      for (const [key, value] of Object.entries(extraHeaders)) {
        result = result.set(key, value);
      }
    }

    return result;
  }

  protected get<T = any>(url, options: HttpReadOptions = {}): Observable<T> {
    const shouldRetry =
      options.retry !== false &&
      !url.toLowerCase().includes('stripe') &&
      !url.toLowerCase().includes('bulkemail');

    let request = this.http.get<{ data: T }>(url, {
      headers: this.createAuthorizationHeader(),
    });

    if (shouldRetry) {
      request = request.pipe(retry(SalonmonsterHttpClient.TRANSIENT_RETRY));
    }

    return request.pipe(
      map((result) => result.data),
      catchError((err: HttpErrorResponse) =>
        throwError(() => this.errorHandlerService.handleError(err))
      )
    );
  }

  protected post<T = any>(
    url,
    data,
    extraHeaders?: Record<string, string>
  ): Observable<T> {
    const shouldRetry = this.isRetryableWrite(url);

    let request = this.http.post<{ data: T }>(url, data, {
      headers: this.createAuthorizationHeader(undefined, extraHeaders),
    });

    if (shouldRetry) {
      request = request.pipe(retry(SalonmonsterHttpClient.TRANSIENT_RETRY));
    }

    return request.pipe(
      map((result) => result.data),
      catchError((err: HttpErrorResponse) =>
        throwError(() => this.errorHandlerService.handleError(err))
      )
    );
  }

  protected put<T = any>(url, data): Observable<T> {
    // PUT reads the same non-retryable list as POST, rather than carrying its
    // own /sales/ test — that test was explicitly a placeholder for exactly
    // this consolidation. A sale update still never retries (resending a
    // Complete re-runs the checkout); so now do the other non-idempotent
    // writes, which previously retried on PUT while being refused on POST.
    return this.http
      .put<{ data: T | any }>(url, data, {
        headers: this.createAuthorizationHeader(),
      })
      .pipe(
        this.isRetryableWrite(url)
          ? retry(SalonmonsterHttpClient.TRANSIENT_RETRY)
          : identity,
        map((result) => result.data),
        catchError((err: HttpErrorResponse) =>
          throwError(() => this.errorHandlerService.handleError(err))
        )
      );
  }

  protected delete<T = any>(url, data): Observable<T> {
    const shouldRetry = this.isRetryableWrite(url);

    let request = this.http.delete<{ data: T }>(url, {
      headers: this.createAuthorizationHeader(),
      body: data,
    });

    if (shouldRetry) {
      request = request.pipe(retry(SalonmonsterHttpClient.TRANSIENT_RETRY));
    }

    return request.pipe(
      map((result) => result.data),
      catchError((err: HttpErrorResponse) =>
        throwError(() => this.errorHandlerService.handleError(err))
      )
    );
  }
}
